import React, { useEffect, useState } from 'react';
import { Target, Euro, Users, Loader2, Plus, Check } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import DatePicker from '@/components/ui/DatePicker';
import { GoalType } from '@/services/goals/goalsService';

const inputCls = 'w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900';
const labelCls = 'block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2';

export interface NewGoalPayload {
    type: GoalType;
    title: string;
    target: number;
    deadline: string | null;
    description: string | null;
}

interface GoalFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (payload: NewGoalPayload) => void | Promise<void>;
    saving?: boolean;
    /** Se presente, il form è in modifica e viene precompilato con questi valori. */
    initial?: NewGoalPayload | null;
}

const toLocalIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Creazione di un obiettivo. Prima si sceglie il tipo (economico o clienti),
 * poi valore, titolo (obbligatorio), descrizione e scadenza (calendario + a mano).
 */
const GoalFormModal: React.FC<GoalFormModalProps> = ({ isOpen, onClose, onCreate, saving = false, initial = null }) => {
    const isEdit = !!initial;
    const [type, setType] = useState<GoalType>('economic');
    const [target, setTarget] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [deadline, setDeadline] = useState(''); // ISO yyyy-mm-dd

    // Reset (o precompilazione in modifica) a ogni apertura.
    useEffect(() => {
        if (isOpen) {
            setType(initial?.type ?? 'economic');
            setTarget(initial ? String(initial.target) : '');
            setTitle(initial?.title ?? '');
            setDescription(initial?.description ?? '');
            setDeadline(initial?.deadline ?? '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const targetNum = parseFloat(target);
    const canSubmit = title.trim().length > 0 && !!targetNum && targetNum > 0 && !saving;

    const submit = () => {
        if (!canSubmit) return;
        onCreate({
            type,
            title: title.trim(),
            target: targetNum,
            deadline: deadline || null,
            description: description.trim() || null,
        });
    };

    const deadlineDate = deadline ? new Date(`${deadline}T00:00:00`) : undefined;

    const typeCard = (value: GoalType, icon: React.ReactNode, label: string, hint: string) => (
        <button
            type="button"
            onClick={() => setType(value)}
            className={`flex items-center gap-3 p-4 rounded-2xl border text-left transition-all ${type === value ? 'border-clip-500 bg-clip-50/60 ring-1 ring-clip-200' : 'border-gray-200 hover:border-gray-300'}`}
        >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${type === value ? 'bg-clip-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {icon}
            </div>
            <div>
                <p className="font-bold text-gray-900 text-sm">{label}</p>
                <p className="text-xs text-gray-500">{hint}</p>
            </div>
        </button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-xl"
            headerClassName="bg-white border-b border-gray-100"
            headerContent={
                <div className="flex items-center gap-3 pr-8">
                    <div className="w-11 h-11 rounded-2xl bg-clip-50 text-clip-600 flex items-center justify-center">
                        <Target size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{isEdit ? 'Modifica obiettivo' : 'Nuovo obiettivo'}</h2>
                        <p className="text-sm text-gray-400">{isEdit ? "Aggiorna i parametri: l'AI rigenererà l'analisi." : "Definisci un traguardo: l'AI lo analizza sui tuoi dati."}</p>
                    </div>
                </div>
            }
        >
            <div className="space-y-5">
                {/* 1. Tipo (prima scelta) */}
                <div>
                    <label className={labelCls}>Tipo di obiettivo</label>
                    <div className="grid grid-cols-2 gap-3">
                        {typeCard('economic', <Euro size={18} />, 'Economico', 'Spesa o investimento')}
                        {typeCard('clients', <Users size={18} />, 'Clienti', 'Crescita clientela')}
                    </div>
                </div>

                {/* 2. Titolo (obbligatorio) */}
                <div>
                    <label className={labelCls}>Titolo</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={type === 'economic' ? 'es. Nuova poltrona' : 'es. Nuovi clienti estate'}
                        className={inputCls}
                    />
                </div>

                {/* 3. Valore */}
                <div>
                    <label className={labelCls}>{type === 'economic' ? 'Importo / investimento (€)' : 'Clienti da raggiungere'}</label>
                    <input
                        type="number"
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                        placeholder={type === 'economic' ? 'es. 5000' : 'es. 100'}
                        className={inputCls}
                    />
                </div>

                {/* 4. Descrizione (facoltativa) */}
                <div>
                    <label className={labelCls}>Descrizione (opzionale)</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={2}
                        placeholder="Aggiungi dettagli utili all'analisi…"
                        className={`${inputCls} resize-none`}
                    />
                </div>

                {/* 5. Scadenza (calendario) */}
                <div>
                    <label className={labelCls}>Scadenza (opzionale)</label>
                    <DatePicker
                        selected={deadlineDate}
                        onSelect={(d) => setDeadline(d ? toLocalIso(d) : '')}
                        minDate={new Date()}
                        allowClosedDays
                        placeholder="Scegli una data"
                    />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-bold text-sm transition-colors"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 px-5 py-2.5 bg-clip-500 hover:bg-clip-600 text-white font-bold rounded-xl text-sm transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : isEdit ? <Check size={16} /> : <Plus size={16} />} {isEdit ? 'Salva modifiche' : 'Crea obiettivo'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default GoalFormModal;
